"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      "inline-flex items-center gap-0.5 border-b border-border bg-transparent p-0 text-muted-foreground",
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base
      "relative inline-flex items-center justify-center gap-1.5 whitespace-nowrap",
      "px-3 pb-2.5 pt-1 text-sm font-medium",
      // Underline indicator via ::after
      "after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px]",
      "after:rounded-full after:bg-primary after:opacity-0 after:transition-opacity after:duration-150",
      // States
      "text-muted-foreground transition-colors duration-150 hover:text-foreground",
      "data-[state=active]:text-foreground data-[state=active]:after:opacity-100",
      // A11y
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      "disabled:pointer-events-none disabled:opacity-50",
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

/** A `TabsList` stacked as a side rail; pair with `orientation="vertical"` on `Tabs`. */
const verticalTabsListClass =
  "flex h-auto w-full flex-col items-stretch gap-1 border-b-0 bg-transparent p-0";

/** A rail row: left-aligned, filled when active instead of underlined. */
const verticalTabTriggerClass =
  "w-full justify-start px-3 py-2 after:hidden data-[state=active]:bg-muted data-[state=active]:text-foreground";

/**
 * Sub-tabs inside a tab: a compact segmented track, as wide as its tabs and
 * left-aligned, styled like AI Analysis's MCA / LOC toggle. Scrolls sideways
 * rather than overflow on a narrow screen.
 */
const segmentedTabsListClass =
  "h-8 w-fit max-w-full justify-start gap-0.5 overflow-x-auto rounded-md border-b-0 bg-muted p-0.5";

/** One segment: tab semantics and arrow keys from the Tabs primitive; raised when active. */
const segmentedTabTriggerClass =
  "h-full shrink-0 rounded-sm px-2.5 py-0 text-[0.65rem] font-bold uppercase tracking-wide " +
  "text-muted-foreground after:hidden hover:text-foreground " +
  "data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm " +
  "focus-visible:ring-offset-0";

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  verticalTabsListClass,
  verticalTabTriggerClass,
  segmentedTabsListClass,
  segmentedTabTriggerClass,
};
